import React, { HTMLAttributes } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";
import clsx from "clsx";
import { skeletonAnimation, skeletonColour, transparentColour } from "theme";
import calculateAspectRatioPaddingTopPercentage from "utils/calculateAspectRatioPaddingTopPercentage";

const useStyles = (hideBackground: boolean) =>
    makeStyles((theme: Theme) => {
        return createStyles({
            root: {
                overflow: "hidden",
                display: "flex",
                flex: "1 0 auto",
                backgroundColor: hideBackground ? transparentColour : skeletonColour,
                position: "relative",
            },
            imageSkeleton: { backgroundColor: hideBackground ? transparentColour : skeletonColour },
            paddingContainer: {
                flex: 1,
            },
            childContainer: {
                position: "absolute",
                top: 0,
                width: "100%",
                height: "100%",
                display: "flex",
            },
        });
    });

type IClasses = Partial<ReturnType<ReturnType<typeof useStyles>>>;

export interface IMediaSkeletonProps extends HTMLAttributes<HTMLDivElement> {
    aspectRatio: number;
    isLoading?: boolean;
    classes?: IClasses;
    hideBackground?: boolean;
}

export default function MediaSkeleton(props: IMediaSkeletonProps) {
    const {
        aspectRatio,
        isLoading = false,
        hideBackground = false,
        className,
        children,
        classes: overrideClasses,
        ...rest
    } = props;

    const classes = useStyles(hideBackground)();

    const paddingTopPercentage = calculateAspectRatioPaddingTopPercentage({
        width: aspectRatio,
        height: 1,
    });

    return (
        <div className={clsx(className, classes.root, overrideClasses?.root)} {...rest}>
            {isLoading ? (
                <Skeleton
                    className={clsx(classes.imageSkeleton, overrideClasses?.imageSkeleton)}
                    animation={skeletonAnimation}
                    variant="rect"
                    width="100%"
                    height={0}
                    style={{ paddingTop: paddingTopPercentage }}
                />
            ) : (
                <div className={clsx(classes.paddingContainer, overrideClasses?.paddingContainer)}>
                    <div style={{ paddingTop: paddingTopPercentage }} />
                    <div className={clsx(classes.childContainer, overrideClasses?.childContainer)}>
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
}
